import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { type AxiosError } from 'axios';

import { apiUri } from '@/constants/Constants';
import { UserPermissions } from '@/constants/permissions.constant';
import { checkIfPmi, showSmbInvite } from '@/utils/commonUtils';
import { hasUserPermission, permissionIsNotSet } from '@/utils/permissions.util';
import { useCurrentUser } from '@/stores/useCurrentUser';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import { type Renter, RenterInviteType } from '@/utils/types/renter.interface';
import type { User } from '@/utils/types/User';
import { InviteLeaseDetailsSegmentEvents } from '../constants';
import { startInviteSubmitAttempt } from '../invite-lease-details.rum';
import { useHasCustomScreeningStatusLabels } from './use-has-custom-screening-status-labels';
import { useInviteTracking } from './use-invite-tracking';
import { DealsDashboardClient } from '@/shared/api';
import type { WorkflowAttempt } from '@/shared/libs';
import {
  buildInviteLeaseDetailsPayload,
  normalizeInviteLeaseDetailsValues,
  applyInviteServiceFieldErrors,
  type SubmitErrorData,
} from '../utils';

const getUserType = (user: User) => {
  if (checkIfPmi(user)) {
    return 'PMI';
  }
  return user.LLType;
};

const countApplicants = (values: Renter) =>
  values.applicantEmails?.filter((applicant) => applicant?.value?.trim()).length ?? 0;

const countFieldErrors = (data?: SubmitErrorData) => data?.field_errors?.length ?? 0;

// A mapping bug is not a request failure: record it on the attempt, then let the form engine
// surface it.
const buildInviteRequest = (
  values: Renter,
  { useNewForm, isSmbInvite }: { useNewForm: boolean; isSmbInvite: boolean },
  attempt: WorkflowAttempt,
) => {
  try {
    const normalizedValues = normalizeInviteLeaseDetailsValues(values, useNewForm, isSmbInvite);
    return { normalizedValues, requestPayload: buildInviteLeaseDetailsPayload(normalizedValues) };
  } catch (error) {
    attempt.fail(error, { failure_stage: 'build_payload' });
    throw error;
  }
};

type UseInviteLeaseDetailsControllerOptions = {
  onSuccess?: (values: Renter) => void;
};

export const useInviteLeaseDetailsController = (
  options?: UseInviteLeaseDetailsControllerOptions,
) => {
  const [errorMsg, setErrorMsg] = useState<string>('');
  const hasTriggeredOnLoadEventsRef = useRef(false);
  const { track } = useAnalyticsContext();
  const { trackInvite, flowId, entryPoint, inviteSegment } = useInviteTracking();

  const { user } = useCurrentUser();

  const isSmbInvite = useMemo(() => (user ? showSmbInvite(user) : false), [user]);
  const hasCustomScreeningStatusLabels = useHasCustomScreeningStatusLabels(isSmbInvite);

  /**
   * Usage of the new SMB form with dynamic coverages
   * Brought by DDS + Pricing Service
   */
  const useNewForm = useMemo(() => {
    /** PMI users are not eligible for the new SMB renter invite form */
    if (!user || checkIfPmi(user)) {
      return false;
    }

    /** If user is not an SMB user, return false */
    if (!isSmbInvite) {
      return false;
    }

    /** Any other case, return true. All SMB users (except PMI) are eligible for the new SMB renter invite form */
    return true;
  }, [user, isSmbInvite]);

  useEffect(() => {
    const shouldTriggerFirstLoadEvents =
      isSmbInvite && !hasTriggeredOnLoadEventsRef.current && Boolean(user);

    if (!shouldTriggerFirstLoadEvents) {
      return;
    }

    hasTriggeredOnLoadEventsRef.current = true;

    const inviteVariant = useNewForm
      ? RenterInviteType.SMBInviteNewLimitedSdr
      : RenterInviteType.SMBInviteOldCustomCoverage;

    track('Renter Invite form Visited', {
      newAbTests: { variant: inviteVariant },
      landlordType: getUserType(user),
      referrer: window.location.pathname,
    });
  }, [useNewForm, track, user, isSmbInvite]);

  const customCoverageDisabled = useMemo(() => {
    if (!user) {
      return false;
    }

    const isPermissionNotSet = permissionIsNotSet(
      UserPermissions.Deal.CanCustomizeCoverages,
      user.userPermissions || [],
    );
    if (isPermissionNotSet) {
      return false;
    }

    return !hasUserPermission(
      UserPermissions.Deal.CanCustomizeCoverages,
      user.userPermissions || [],
    );
  }, [user]);

  const trackServerValidationErrors = useCallback(
    (errors: Array<{ formField: string; message: string }>) => {
      if (errors.length === 0) {
        return;
      }
      trackInvite(InviteLeaseDetailsSegmentEvents.ValidationErrorsShown, {
        validation_source: 'server',
        error_category: 'server_validation',
        errors: errors.map(({ formField, message }) => ({
          field_name: formField,
          error_code: 'server_validation',
          error_message_shown: message,
        })),
      });
    },
    [trackInvite],
  );

  const onSubmit = useCallback(
    async (
      values: Renter,
      setError: UseFormSetError<Renter>,
      submitAttemptNumber?: number,
      startedAttempt?: WorkflowAttempt,
    ) => {
      setErrorMsg('');

      const attemptNumber = submitAttemptNumber ?? 1;
      const attempt =
        startedAttempt ??
        startInviteSubmitAttempt({
          flowId,
          entryPoint,
          inviteSegment,
          attemptNumber,
          applicantCount: countApplicants(values),
        });
      const { normalizedValues, requestPayload } = buildInviteRequest(
        values,
        { useNewForm, isSmbInvite },
        attempt,
      );

      const submitProps = {
        property_id: normalizedValues.address?.property_id,
        screening_label_id: normalizedValues.screeningStatus,
        has_custom_screening_status_labels: hasCustomScreeningStatusLabels(
          normalizedValues.address?.property_id,
        ),
        submit_attempt_number: attemptNumber,
      };

      const handleInviteServiceError = (data: SubmitErrorData | undefined) => {
        if (data?.field_errors?.length) {
          const applied = applyInviteServiceFieldErrors(data.field_errors, setError);
          trackServerValidationErrors(applied);
          setErrorMsg('Please correct the errors above.');
          return;
        }
        setErrorMsg(data?.message || 'An error occurred while sending the invite.');
      };

      try {
        await DealsDashboardClient.post(apiUri.inviteViaDealsService, requestPayload);
      } catch (error_) {
        const errorData = (error_ as AxiosError<SubmitErrorData> | null | undefined)?.response
          ?.data;
        attempt.fail(error_, {
          failure_stage: 'request',
          field_error_count: countFieldErrors(errorData),
        });
        trackInvite(InviteLeaseDetailsSegmentEvents.Submit, { ...submitProps, outcome: 'failure' });
        handleInviteServiceError(errorData);
        return;
      }

      attempt.succeed();
      trackInvite(InviteLeaseDetailsSegmentEvents.Submit, { ...submitProps, outcome: 'success' });
      // Outside the try: the invite was sent, so a failing success handler must not be reported or
      // shown as a failed invite.
      options?.onSuccess?.(values);
    },
    [
      useNewForm,
      isSmbInvite,
      options,
      hasCustomScreeningStatusLabels,
      trackInvite,
      trackServerValidationErrors,
      flowId,
      entryPoint,
      inviteSegment,
    ],
  );

  return {
    user,
    errorMsg,
    isSmbInvite,
    useNewForm,
    customCoverageDisabled,
    onSubmit,
  };
};

export type UseInviteLeaseDetailsControllerResult = ReturnType<
  typeof useInviteLeaseDetailsController
>;
